import './index.scss';
import React, {lazy, useCallback, useEffect, useMemo, useRef} from "react";
import ElementIds from "../../core/models/element-ids";
import LoadingBar, {LoadingBarRef} from "react-top-loading-bar";
import {FadeAnimationLayout, LoadingBarTrigger, LoadingBarTriggerContextValue, LoadingBarTriggerProvider, useAuthorizationToken} from '../../@bizkeytech';
import AppRoutes from "../../core/models/routes";
import {matchRoutes, Navigate, Route, Routes, useLocation} from "react-router-dom";
import {Box} from '@mui/material';
import CachingService from "../../core/services/caching";
import ApiService from "../../core/services/api";


const AuthenticationViews = lazy(() => import("./public/authentication"));
const LoginAuthenticationView = lazy(() => import("./public/authentication/login"));
const LogoutAuthenticationView = lazy(() => import("./public/authentication/logout"));
const RedirectAuthenticationView = lazy(() => import("./public/authentication/redirect"));

const ErrorViews = lazy(() => import("./public/error"));
const NotFoundErrorView = lazy(() => import("./public/error/not-found"));
const AccessDeniedErrorView = lazy(() => import("./public/error/access-denied"));
const ServerErrorErrorView = lazy(() => import("./public/error/server-error"));

const VersionHistoryView = lazy(() => import("./private/version-history"));

const TestView = lazy(() => import("./public/test"));

const PanelViews = lazy(() => import("./panel"));
const PanelSettingsView = lazy(() => import("./panel/settings"));
const PanelReportsView = lazy(() => import("./panel/reports"));

const PanelEmployeesView = lazy(() => import("./panel/employees"));
const PanelEmployeeInformationView = lazy(() => import("./panel/employee-information"));
const PanelEmployeeInformationViewGeneralInformationTab = lazy(() => import("./panel/employee-information/general-information"));

const ApplicationViews = () => {
    const location = useLocation();
    const loadingBarRef = useRef<LoadingBarRef | null>(null);
    const authorizationToken = useAuthorizationToken();

    /**
     * Fetches the biz file system config from the server.
     */
    const getBizFileSystemConfig = useCallback<AsyncCallback<void>>(async () => {
        if (!!CachingService.getBizFileFileBaseUrl())
            return;
        const response = await ApiService.v1.bizFile.createAccessToken(authorizationToken);
        if (response?.resultFlag && response.data) {
            CachingService.setBizFileFileBaseUrl(response.data.baseURL);
        }
    }, [authorizationToken])

    /**
     * As soon as the component is mounted:
     * - fetches the biz file system config from the server.
     */
    useEffect(() => {
        void getBizFileSystemConfig()
    }, [getBizFileSystemConfig])

    /**
     * Starts the animation of the LoadingBar component.
     */
    const onRoutesLoadingStarted = useCallback<LoadingBarTriggerContextValue['onStart']>(() => {
        loadingBarRef.current?.continuousStart?.(0, 300);
    }, [loadingBarRef])

    /**
     * Completes the animation of the LoadingBar component.
     */
    const onRoutesLoadingFinished = useCallback<LoadingBarTriggerContextValue['onFinish']>(() => {
        loadingBarRef.current?.complete?.();
    }, [loadingBarRef])

    const motionKey = useMemo<string>(() => {
        const nonPanelRoutes: string[] = [
            AppRoutes.public.error._base,
            AppRoutes.public.authentication._base,
            AppRoutes.public.test,
        ];
        if (!!matchRoutes(
            nonPanelRoutes.map(e => ({path: e, end: false})),
            location.pathname
        )) {
            return location.pathname
        }
        return "panel";

    }, [location.pathname])

    return (
        <LoadingBarTriggerProvider value={{onStart: onRoutesLoadingStarted, onFinish: onRoutesLoadingFinished}}>
            <div className={'application-view'}>
                <FadeAnimationLayout motionKey={motionKey} id={ElementIds.appId} className={'app'}>
                    <LoadingBar
                        ref={loadingBarRef}
                        containerClassName={'loading-bar-container'}
                        className={'loading-bar'}
                        color={'transparent'}
                    />
                    <LoadingBarTrigger
                        fallback={
                            <Box
                                sx={{
                                    minHeight: '100vh',
                                    width: '100%',
                                    '@supports (-webkit-touch-callout: none)': {
                                        minHeight: '-webkit-fill-available',
                                    },
                                }}
                            />
                        }
                    >
                        <Routes>
                            {/* --- Authentication Views ---*/}
                            <Route path={AppRoutes.public.authentication.base} element={<AuthenticationViews/>}>
                                <Route path={AppRoutes.public.authentication._relative.login} element={<LoginAuthenticationView/>}/>
                                <Route path={AppRoutes.public.authentication._relative.logout} element={<LogoutAuthenticationView/>}/>
                                <Route path={AppRoutes.public.authentication._relative.redirect} element={<RedirectAuthenticationView/>}/>
                                <Route index element={<Navigate to={AppRoutes.public.authentication.redirect} replace/>}/>
                                <Route path="*" element={<Navigate to={AppRoutes.public.error.base} replace/>}/>
                            </Route>

                            {/* --- Private Views ---*/}
                            <Route path={AppRoutes.private.versionHistory} element={<VersionHistoryView/>}/>

                            {/* --- Panel Views ---*/}
                            <Route path={AppRoutes.panel.base} element={<PanelViews/>}>
                                <Route path={AppRoutes.panel._relative.reports} element={<PanelReportsView/>}/>
                                <Route path={AppRoutes.panel._relative.settings} element={<PanelSettingsView/>}/>
                                <Route path={AppRoutes.panel._relative.employees} element={<PanelEmployeesView/>}/>
                                <Route path={AppRoutes.panel._relative.employeeInformation} element={<PanelEmployeeInformationView/>}>
                                    <Route
                                        path={AppRoutes.panel.employeeInformation._relative.generalInformation}
                                        element={<PanelEmployeeInformationViewGeneralInformationTab/>}
                                    />
                                </Route>
                                <Route index element={<Navigate to={AppRoutes.panel.settings} replace/>}/>
                            </Route>

                            {/* --- Other Routes ---*/}
                            <Route path={AppRoutes.public.landing} element={<Navigate to={AppRoutes.panel.base} replace/>}/>
                            <Route path={AppRoutes.public.test} element={<TestView/>}/>

                            {/* --- Error Views ---*/}
                            <Route path={AppRoutes.public.error.base} element={<ErrorViews/>}>
                                <Route path={AppRoutes.public.error._relative.notFound} element={<NotFoundErrorView/>}/>
                                <Route path={AppRoutes.public.error._relative.accessDenied} element={<AccessDeniedErrorView/>}/>
                                <Route path={AppRoutes.public.error._relative.serverError} element={<ServerErrorErrorView/>}/>
                                <Route index element={<Navigate to={AppRoutes.public.error.notFound} replace/>}/>
                                <Route path="*" element={<Navigate to={AppRoutes.public.error.notFound} replace/>}/>
                            </Route>

                            {/* --- Fallback Route ---*/}
                            <Route path="*" element={<Navigate to={AppRoutes.public.error.base} replace/>}/>
                        </Routes>
                    </LoadingBarTrigger>
                </FadeAnimationLayout>
            </div>
        </LoadingBarTriggerProvider>
    );
}

export default ApplicationViews;
